import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InfoDialog } from './components/InfoDialog';
import { EclipseTimeline } from './components/EclipseTimeline';
import { LayerPanel, type LayerState } from './components/LayerPanel';
import { MapView } from './components/MapView';
import { SelectedLocationPanel } from './components/SelectedLocationPanel';
import { useClock } from './hooks/useClock';
import { useTerrainVisibility } from './hooks/useTerrainVisibility';
import { changeLanguage, type AppLanguage } from './i18n';
import {
  ECLIPSE_PREVIEW_TIME,
  ECLIPSE_TIMELINE_START,
  getCountdownParts,
  getLocalEclipseDetails,
  getLocalEclipseMoment,
} from './lib/eclipse';
import {
  cloudAtLocation,
  fetchWeatherManifest,
  FORECAST_FRAME_TOLERANCE_MS,
  selectWeatherFrame,
  type CloudReading,
  type WeatherLayer,
  type WeatherManifest,
  weatherRefreshInterval,
} from './lib/weather';
import type { Coordinate, RouteResult } from './types';

type WeatherStatus = 'idle' | 'loading' | 'ready' | 'unavailable' | 'error';

const defaultLayers: LayerState = {
  totality: true,
  obscuration: true,
  clouds: true,
  traffic: false,
  terrain: true,
};

export default function App() {
  const { t, i18n } = useTranslation();
  const now = useClock();
  const [layers, setLayers] = useState(defaultLayers);
  const [layerPanelOpen, setLayerPanelOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [selected, setSelected] = useState<Coordinate | null>(null);
  const [origin, setOrigin] = useState<Coordinate | null>(null);
  const [pickingOrigin, setPickingOrigin] = useState(false);
  const [route, setRoute] = useState<RouteResult | null>(null);
  const [previewSeconds, setPreviewSeconds] = useState(
    (ECLIPSE_PREVIEW_TIME.getTime() - ECLIPSE_TIMELINE_START.getTime()) / 1000,
  );
  const [weatherManifest, setWeatherManifest] = useState<WeatherManifest | null>(null);
  const [weatherLoadStatus, setWeatherLoadStatus] = useState<WeatherStatus>('idle');
  const [selectedCloud, setSelectedCloud] = useState<CloudReading | null>(null);
  const [weatherRefresh, setWeatherRefresh] = useState(0);

  const forecastTime = useMemo(
    () => new Date(ECLIPSE_TIMELINE_START.getTime() + previewSeconds * 1000),
    [previewSeconds],
  );
  const displayTime = forecastTime;
  const details = useMemo(
    () => (selected ? getLocalEclipseDetails({ ...selected, elevation: 0 }) : null),
    [selected],
  );
  const eclipseMoment = useMemo(
    () => (selected ? getLocalEclipseMoment({ ...selected, elevation: 0 }, displayTime) : null),
    [displayTime, selected],
  );
  const terrain = useTerrainVisibility(
    selected,
    details?.sunAltitude ?? null,
    details?.sunAzimuth ?? null,
    layers.terrain,
  );
  const countdown = getCountdownParts(now);
  const weatherFrame = useMemo(
    () =>
      weatherManifest
        ? selectWeatherFrame(weatherManifest, displayTime, FORECAST_FRAME_TOLERANCE_MS)
        : null,
    [displayTime, weatherManifest],
  );
  const weatherLayer = useMemo<WeatherLayer | null>(
    () =>
      weatherManifest && weatherFrame
        ? {
            frame: weatherFrame,
            bounds: weatherManifest.bounds,
            opacityExponent: weatherManifest.cloudEncoding.opacityExponent,
          }
        : null,
    [weatherFrame, weatherManifest],
  );
  const cloudStatus: WeatherStatus = !layers.clouds
    ? 'idle'
    : weatherLoadStatus === 'ready' && !weatherLayer
      ? 'unavailable'
      : weatherLoadStatus;

  useEffect(() => {
    let timer = 0;
    const schedule = () => {
      timer = window.setTimeout(() => {
        setWeatherRefresh((value) => value + 1);
        schedule();
      }, weatherRefreshInterval(new Date()));
    };
    schedule();
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!layers.clouds) {
      setWeatherLoadStatus('idle');
      return;
    }
    let cancelled = false;
    setWeatherLoadStatus('loading');
    void fetchWeatherManifest()
      .then((manifest) => {
        if (!cancelled) {
          setWeatherManifest(manifest);
          setWeatherLoadStatus('ready');
        }
      })
      .catch(() => {
        if (!cancelled) setWeatherLoadStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [layers.clouds, weatherRefresh]);

  useEffect(() => {
    if (!selected || !weatherLayer || !layers.clouds) {
      setSelectedCloud(null);
      return;
    }
    let cancelled = false;
    setSelectedCloud(null);
    void cloudAtLocation(weatherLayer, selected)
      .then((reading) => {
        if (!cancelled) setSelectedCloud(reading);
      })
      .catch(() => {
        if (!cancelled) setSelectedCloud(null);
      });
    return () => {
      cancelled = true;
    };
  }, [layers.clouds, selected, weatherLayer]);

  const selectOnMap = (coordinate: Coordinate) => {
    if (pickingOrigin) {
      setOrigin(coordinate);
      setPickingOrigin(false);
      setRoute(null);
      return;
    }
    setSelected(coordinate);
    setRoute(null);
  };

  const toggleLanguage = () => {
    const language: AppLanguage = i18n.language.startsWith('es') ? 'en' : 'es';
    void changeLanguage(language);
  };

  return (
    <main className={`app-shell ${selected ? 'has-selection' : ''}`}>
      <MapView
        layers={layers}
        selected={selected}
        origin={origin}
        obscurationTime={displayTime}
        route={route?.geometry ?? null}
        weather={weatherLayer}
        pickingOrigin={pickingOrigin}
        onSelect={selectOnMap}
      />

      <header className="top-bar">
        <div className="brand-lockup">
          <span className="brand-eclipse" aria-hidden="true" />
          <div>
            <h1>{t('appName')}</h1>
            <p>{t('subtitle')}</p>
          </div>
        </div>
        <div className="top-actions">
          <button className="utility-button language-button" type="button" onClick={toggleLanguage}>
            {t('language')}
          </button>
          <button
            className="utility-button layers-button"
            type="button"
            onClick={() => setLayerPanelOpen((value) => !value)}
            aria-expanded={layerPanelOpen}
            aria-controls="map-layer-panel"
          >
            <span aria-hidden="true">≡</span> <span>{t('openLayers')}</span>
          </button>
        </div>
      </header>

      {!selected && !pickingOrigin && <div className="map-prompt">{t('map.selectPrompt')}</div>}
      {pickingOrigin && <div className="map-prompt origin-prompt">{t('map.originPrompt')}</div>}

      {layers.clouds && weatherManifest && weatherFrame && (
        <aside className="weather-key" aria-label={t('weather.legend')}>
          <div className="weather-key-heading">
            <strong>{t('weather.legend')}</strong>
            <span>{weatherManifest.resolutionKm} km</span>
          </div>
          <div className="weather-gradient" aria-hidden="true" />
          <div className="weather-scale">
            <span>0%</span>
            <span>100%</span>
          </div>
          <small>
            ICON‑EU ·{' '}
            {new Intl.DateTimeFormat(i18n.language === 'es' ? 'es-ES' : 'en-GB', {
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
              timeZone: 'Europe/Madrid',
            }).format(new Date(weatherFrame.validAt))}
          </small>
        </aside>
      )}

      {layers.clouds && cloudStatus === 'unavailable' && (
        <aside className="weather-key forecast-gate" aria-live="polite">
          <div className="weather-key-heading">
            <strong>{t('weather.forecast')}</strong>
            <span>ICON‑EU</span>
          </div>
          <p>{t('weather.unavailable')}</p>
          <small>{t('weather.forecastDate')}</small>
        </aside>
      )}

      {layers.clouds && cloudStatus === 'error' && (
        <aside className="weather-key forecast-gate weather-error" role="status">
          <div className="weather-key-heading">
            <strong>{t('weather.forecast')}</strong>
            <span>ICON‑EU</span>
          </div>
          <p>{t('weather.error')}</p>
        </aside>
      )}

      <EclipseTimeline
        displayTime={displayTime}
        countdown={countdown}
        details={details}
        moment={eclipseMoment}
        onTimeChange={(date) =>
          setPreviewSeconds((date.getTime() - ECLIPSE_TIMELINE_START.getTime()) / 1000)
        }
      />

      <LayerPanel
        open={layerPanelOpen}
        layers={layers}
        onChange={setLayers}
        onClose={() => setLayerPanelOpen(false)}
      />

      {selected && details && (
        <SelectedLocationPanel
          location={selected}
          details={details}
          moment={eclipseMoment}
          displayTime={displayTime}
          cloud={selectedCloud}
          cloudStatus={cloudStatus}
          terrain={terrain}
          origin={origin}
          pickingOrigin={pickingOrigin}
          onClose={() => {
            setSelected(null);
            setRoute(null);
          }}
          onOriginChange={setOrigin}
          onPickingOriginChange={setPickingOrigin}
          onRouteChange={setRoute}
        />
      )}

      <button
        className="info-button"
        type="button"
        onClick={() => {
          setLayerPanelOpen(false);
          setInfoOpen(true);
        }}
      >
        ⓘ {t('info.open')}
      </button>
      <InfoDialog open={infoOpen} onClose={() => setInfoOpen(false)} />
    </main>
  );
}
